import { useState, useRef, useEffect } from 'react';
import { useChartStore } from '@/store/chartStore';
import { ADJUSTMENT_ITEMS } from './adjustmentItems';
import { HeaderPopover } from './HeaderPopover';
import type { PriceAdjustmentType } from '@/types';

export function AdjustmentSelector() {
  const priceAdjustments    = useChartStore((s) => s.priceAdjustments);
  const setPriceAdjustments = useChartStore((s) => s.setPriceAdjustments);

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handler(e: PointerEvent) {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [open]);

  function toggle(value: PriceAdjustmentType) {
    const next = priceAdjustments.includes(value)
      ? priceAdjustments.filter((v) => v !== value)
      : [...priceAdjustments, value];
    setPriceAdjustments(next);
  }

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        title="Adjust for corporate actions"
        className={`hidden md:flex items-center justify-center p-1.5 rounded text-sm font-medium transition-colors ${
          open ? 'bg-accent/20 text-accent' : 'text-muted hover:text-text hover:bg-panel-hover'
        }`}
      >
        Adj.
      </button>

      <HeaderPopover
        ref={panelRef}
        anchorRef={ref}
        open={open}
        align="right"
        className="mt-1 w-52 glass-modal rounded-xl z-dropdown py-1.5 animate-menu-enter"
      >
        <p className="px-3 pb-1.5 pt-0.5 text-[11px] uppercase tracking-wide text-modal-muted">Adjust for</p>
        {ADJUSTMENT_ITEMS.map(({ value, label, note }) => {
          const checked = priceAdjustments.includes(value);
          return (
            <label
              key={value}
              title={note}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-modal-hover"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(value)}
                className="rounded border-border accent-[var(--color-accent)] h-4 w-4 cursor-pointer"
              />
              <span className={checked ? 'text-modal-text font-medium' : 'text-modal-muted'}>{label}</span>
              {note && (
                <span className="text-[10px] uppercase tracking-wide text-muted/70 ml-auto">approx</span>
              )}
            </label>
          );
        })}
      </HeaderPopover>
    </div>
  );
}
